/**
 * India Bazaar ZA - Luxury Slide-Out Cart Drawer & Coupon Management
 */
(function() {
  'use strict';

  function formatMoney(cents) {
    if (typeof cents !== 'number') cents = parseInt(cents, 10) || 0;
    return 'R ' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  const REMOVE_ICON_SVG = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" aria-hidden="true" focusable="false" role="presentation" class="icon icon-remove">
      <path d="M14 3h-3.53a3.07 3.07 0 00-.6-1.65C9.44.82 8.8.5 8 .5s-1.44.32-1.87.85A3.06 3.06 0 005.53 3H2a.5.5 0 000 1h1.25v10c0 .28.22.5.5.5h8.5a.5.5 0 00.5-.5V4H14a.5.5 0 000-1zM6.91 1.98c.23-.29.58-.48 1.09-.48s.85.19 1.09.48c.2.24.3.6.36 1.02h-2.9c.05-.42.17-.78.36-1.02zm4.84 11.52h-7.5V4h7.5v9.5z" fill="currentColor"/>
      <path d="M6.55 5.25a.5.5 0 00-.5.5v6a.5.5 0 001 0v-6a.5.5 0 00-.5-.5zM9.45 5.25a.5.5 0 00-.5.5v6a.5.5 0 001 0v-6a.5.5 0 00-.5-.5z" fill="currentColor"/>
    </svg>
  `;

  class LuxuryCartDrawer {
    constructor() {
      this.drawer = document.getElementById('CartDrawer');
      this.backdrop = document.getElementById('CartDrawerBackdrop');
      this.closeBtn = document.getElementById('CartDrawerCloseBtn');
      this.clearBtn = document.getElementById('CartDrawerClearBtn');
      this.bodyContainer = document.getElementById('CartDrawerBody');
      this.itemsList = document.getElementById('CartDrawerItemsList');
      this.emptyState = document.getElementById('CartDrawerEmpty');
      this.footer = document.getElementById('CartDrawerFooter');
      this.countBadge = document.getElementById('CartDrawerCountBadge');

      // Pricing & totals
      this.subtotalAmount = document.getElementById('CartDrawerSubtotalAmount');
      this.originalTotal = document.getElementById('CartDrawerOriginalTotal');
      this.savingsBadge = document.getElementById('CartDrawerSavingsBadge');
      this.checkoutPrice = document.getElementById('CartDrawerCheckoutPrice');
      this.checkoutBtn = document.getElementById('CartDrawerCheckoutBtn');
      this.checkoutForm = document.getElementById('CartDrawerForm');
      this.isCheckingOut = false;
      this.hasPrefetchedCheckout = false;

      // Coupon section
      this.couponInput = document.getElementById('CartDrawerCouponInput');
      this.couponApplyBtn = document.getElementById('CartDrawerCouponApplyBtn');
      this.couponMsg = document.getElementById('CartDrawerCouponMsg');
      this.appliedDiscountsContainer = document.getElementById('CartDrawerAppliedDiscounts');
      this.discountHiddenInput = document.getElementById('CartDrawerDiscountHidden');

      // Toast elements
      this.toast = document.getElementById('CartToast');
      this.toastImage = document.getElementById('CartToastImage');
      this.toastProductName = document.getElementById('CartToastProductName');
      this.toastCloseBtn = document.getElementById('CartToastCloseBtn');
      this.toastViewCartBtn = document.getElementById('CartToastViewCartBtn');
      this.toastTimer = null;

      this.isUpdating = false;
      this.init();
    }

    init() {
      if (!this.drawer) return;

      // Close handlers
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.close();
        });
      }

      // Clear All handler
      if (this.clearBtn) {
        this.clearBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.clearAll();
        });
      }

      if (this.backdrop) {
        this.backdrop.addEventListener('click', (e) => {
          e.preventDefault();
          this.close();
        });
      }

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.close();
        }
      });

      // Intercept Cart Icon clicks
      this.bindCartTriggers();

      // Delegate Click and Change events inside Drawer
      if (this.drawer) {
        this.drawer.addEventListener('click', this.handleDrawerClick.bind(this));
        this.drawer.addEventListener('change', this.handleDrawerChange.bind(this));
      }

      // Coupon Input handlers
      if (this.couponApplyBtn) {
        this.couponApplyBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.applyCoupon();
        });
      }

      if (this.couponInput) {
        this.couponInput.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            this.applyCoupon();
          }
        });
      }

      // Toast button handlers
      if (this.toastCloseBtn) {
        this.toastCloseBtn.addEventListener('click', () => this.hideToast());
      }
      if (this.toastViewCartBtn) {
        this.toastViewCartBtn.addEventListener('click', () => {
          this.hideToast();
          this.open();
        });
      }

      // Restore any applied coupon from session
      const savedCode = sessionStorage.getItem('ib_cart_discount');
      if (savedCode && this.discountHiddenInput) {
        this.discountHiddenInput.value = savedCode;
      }

      // Observe footer mutations to clean up duplicate prices
      if (this.footer) {
        this.footerObserver = new MutationObserver(() => {
          if (this.isSanitizing) return;
          this.isSanitizing = true;
          try {
            this.sanitizePricingDisplays();
          } finally {
            setTimeout(() => { this.isSanitizing = false; }, 60);
          }
        });
        this.footerObserver.observe(this.footer, { childList: true, subtree: true });
      }

      // Checkout button & form submit handler (Instant visual feedback + Direct fast navigation)
      if (this.checkoutBtn) {
        this.checkoutBtn.addEventListener('click', (e) => {
          this.proceedToCheckout(e);
        });
      }
      if (this.checkoutForm) {
        this.checkoutForm.addEventListener('submit', (e) => {
          this.proceedToCheckout(e);
        });
      }

      // Reset loading state on browser back/forward cache navigation
      window.addEventListener('pageshow', (e) => {
        this.isCheckingOut = false;
        if (this.checkoutBtn) {
          this.checkoutBtn.classList.remove('is-loading');
          const textSpan = this.checkoutBtn.querySelector('.cart-drawer__checkout-text');
          if (textSpan) textSpan.textContent = 'PROCEED TO CHECKOUT';
        }
      });

      this.sanitizePricingDisplays();
      this.refresh();
    }

    bindCartTriggers() {
      const handleCartClick = (e) => {
        const cartTrigger = e.target.closest('#cart-icon-bubble, .wbhcart, [data-cart-drawer-trigger]');
        if (!cartTrigger) return;
        if (e.target.closest('#CartDrawer')) return;

        // Open the drawer site-wide (both Desktop and Mobile)
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        // Close any open bootstrap dropdowns
        const openDropdowns = document.querySelectorAll('.dropdown-menu.show');
        openDropdowns.forEach(dd => dd.classList.remove('show'));

        this.open();
      };

      document.addEventListener('click', handleCartClick, true);
      document.addEventListener('touchstart', (e) => {
        const cartTrigger = e.target.closest('#cart-icon-bubble, .wbhcart, [data-cart-drawer-trigger]');
        if (cartTrigger && !e.target.closest('#CartDrawer')) {
          e.stopPropagation();
        }
      }, { passive: true, capture: true });
    }

    isOpen() {
      return this.drawer && this.drawer.classList.contains('open');
    }

    open() {
      if (!this.drawer) return;
      this.drawer.classList.add('open');
      if (this.backdrop) this.backdrop.classList.add('open');
      document.body.classList.add('cart-drawer-open');
      this.hideToast();
      this.prefetchCheckout();
      this.sanitizePricingDisplays();
      this.refresh(); // Fetch latest cart state and update drawer
    }

    prefetchCheckout() {
      if (this.hasPrefetchedCheckout) return;
      this.hasPrefetchedCheckout = true;
      try {
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = '/checkout';
        document.head.appendChild(link);
      } catch (_) {}
    }

    close() {
      if (!this.drawer) return;
      this.drawer.classList.remove('open');
      if (this.backdrop) this.backdrop.classList.remove('open');
      document.body.classList.remove('cart-drawer-open');
    }

    handleDrawerClick(e) {
      // 1. Remove Item Button
      const removeBtn = e.target.closest('.cart-drawer-item__remove-btn');
      if (removeBtn) {
        e.preventDefault();
        const key = removeBtn.getAttribute('data-key');
        if (key) this.updateQuantity(key, 0);
        return;
      }

      // 2. Stepper Buttons (+ / -)
      const stepBtn = e.target.closest('.cart-drawer-item__step-btn');
      if (stepBtn) {
        e.preventDefault();
        const action = stepBtn.getAttribute('data-action');
        const key = stepBtn.getAttribute('data-key');
        const input = stepBtn.parentElement.querySelector('.cart-drawer-item__qty-input');
        if (!input || !key) return;

        let currentQty = parseInt(input.value, 10) || 0;
        let newQty = action === 'increase' ? currentQty + 1 : Math.max(0, currentQty - 1);
        input.value = newQty;
        this.queueQuantityUpdate(key, newQty);
        return;
      }

      // 3. Remove Applied Discount Pill
      const removeDiscountBtn = e.target.closest('.cart-drawer__applied-discount-remove');
      if (removeDiscountBtn) {
        e.preventDefault();
        const code = removeDiscountBtn.getAttribute('data-code');
        this.removeCoupon(code);
        return;
      }
    }

    handleDrawerChange(e) {
      const input = e.target.closest('.cart-drawer-item__qty-input');
      if (input) {
        const key = input.getAttribute('data-key');
        const newQty = Math.max(0, parseInt(input.value, 10) || 0);
        if (key) this.queueQuantityUpdate(key, newQty);
      }
    }

    async proceedToCheckout(e) {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      if (this.isCheckingOut) return;
      this.isCheckingOut = true;

      // 1. Instant tactile & visual feedback (<16ms)
      if (this.checkoutBtn) {
        this.checkoutBtn.classList.add('is-loading');
        const textSpan = this.checkoutBtn.querySelector('.cart-drawer__checkout-text');
        if (textSpan) textSpan.textContent = 'PROCEEDING...';
      }

      // 2. If any quantity debounce timers are active, flush them immediately
      if (this.qtyDebounceTimers) {
        const pendingKeys = Object.keys(this.qtyDebounceTimers);
        let hadPending = false;
        pendingKeys.forEach(k => {
          if (this.qtyDebounceTimers[k]) {
            hadPending = true;
            clearTimeout(this.qtyDebounceTimers[k]);
            this.qtyDebounceTimers[k] = null;
          }
        });
        if (hadPending || this.isUpdating) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }

      // 3. Build direct checkout destination (skipping slow POST /cart round-trip)
      const discount = (this.discountHiddenInput && this.discountHiddenInput.value.trim()) || sessionStorage.getItem('ib_cart_discount') || '';
      let checkoutUrl = '/checkout';
      if (discount) {
        checkoutUrl += '?discount=' + encodeURIComponent(discount);
      }

      // 4. Instant navigation
      try {
        window.location.assign(checkoutUrl);
      } catch (err) {
        if (this.checkoutForm) {
          this.checkoutForm.submit();
        }
      }
    }

    queueQuantityUpdate(key, quantity) {
      if (!this.qtyDebounceTimers) this.qtyDebounceTimers = {};
      clearTimeout(this.qtyDebounceTimers[key]);

      const itemCard = document.getElementById(`CartDrawerItem-${key}`);
      if (itemCard) itemCard.classList.add('loading');

      this.qtyDebounceTimers[key] = setTimeout(() => {
        this.updateQuantity(key, quantity);
      }, 200);
    }

    updateQuantity(key, quantity) {
      if (this.isUpdating) return;
      this.isUpdating = true;

      const itemCard = document.getElementById(`CartDrawerItem-${key}`);
      if (itemCard) itemCard.classList.add('loading');

      fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ id: key, quantity: quantity })
      })
      .then(res => res.json())
      .then(cart => {
        // Guard: If Shopify returned an error status (e.g. 422 inventory limit), DO NOT wipe the cart!
        if (!cart || cart.status || !Array.isArray(cart.items)) {
          if (typeof this.showCouponMessage === 'function') {
            this.showCouponMessage(cart.description || cart.message || 'Cannot update quantity', 'error');
          }
          this.refresh();
          return;
        }

        this.renderCart(cart);
        this.syncHeaderCart(cart.item_count);
      })
      .catch(err => {
        console.error('Error updating cart quantity:', err);
      })
      .finally(() => {
        this.isUpdating = false;
        if (itemCard) itemCard.classList.remove('loading');
      });
    }

    refresh() {
      fetch('/cart.js')
        .then(res => res.json())
        .then(cart => {
          if (!cart || cart.status || !Array.isArray(cart.items)) return;
          this.renderCart(cart);
          this.syncHeaderCart(cart.item_count);
        })
        .catch(err => console.error('Error refreshing cart:', err));
    }

    async clearAll() {
      if (this.isUpdating) return;
      if (this.clearBtn) {
        this.clearBtn.disabled = true;
        this.clearBtn.classList.add('loading');
        this.clearBtn.textContent = 'Clearing...';
      }
      this.isUpdating = true;
      try {
        const response = await fetch('/cart/clear.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });
        const cart = await response.json();

        this.renderCart(cart);
        this.syncHeaderCart(0);

        if (typeof this.showNotification === 'function') {
          this.showNotification('Your cart has been cleared', 'success');
        }
      } catch (err) {
        console.error('Error clearing cart:', err);
      } finally {
        this.isUpdating = false;
        if (this.clearBtn) {
          this.clearBtn.disabled = false;
          this.clearBtn.classList.remove('loading');
          this.clearBtn.textContent = 'Clear All';
        }
      }
    }

    applyCoupon() {
      if (!this.couponInput) return;
      const code = this.couponInput.value.trim();
      if (!code) {
        this.showCouponMessage('Please enter a coupon code', 'error');
        return;
      }

      if (this.couponApplyBtn) {
        this.couponApplyBtn.disabled = true;
        this.couponApplyBtn.innerHTML = '<span class="cart-drawer-spinner"></span>';
      }

      // Hit Shopify's discount URL endpoint to establish session/checkout discount
      fetch('/discount/' + encodeURIComponent(code), {
        method: 'GET',
        credentials: 'same-origin'
      })
      .then(() => {
        // Also call /cart/update.js with { discount: code } to immediately update cart object
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ discount: code })
        });
      })
      .then(res => res.json())
      .then(cart => {
        sessionStorage.setItem('ib_cart_discount', code);
        if (this.discountHiddenInput) {
          this.discountHiddenInput.value = code;
        }
        this.showCouponMessage(`Coupon "${code}" applied!`, 'success');
        this.couponInput.value = '';
        this.renderCart(cart);
        this.syncHeaderCart(cart.item_count);
      })
      .catch(err => {
        console.error('Error applying coupon:', err);
        this.showCouponMessage('Could not apply coupon. Please try again.', 'error');
      })
      .finally(() => {
        if (this.couponApplyBtn) {
          this.couponApplyBtn.disabled = false;
          this.couponApplyBtn.innerHTML = '<span>Apply</span>';
        }
      });
    }

    removeCoupon(code) {
      if (this.couponMsg) {
        this.showCouponMessage('Removing discount...', 'info');
      }

      // Smooth visual exit on the clicked pill
      const pill = this.appliedDiscountsContainer ? this.appliedDiscountsContainer.querySelector(`[data-code="${code}"]`) : null;
      if (pill) {
        pill.classList.add('removing');
      }

      // 1. Hit Shopify discount CLEAR endpoint
      fetch('/discount/CLEAR', {
        method: 'GET',
        credentials: 'same-origin'
      })
      .catch(() => {})
      .then(() => {
        // 2. Clear discount attribute from cart object
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ discount: '' })
        });
      })
      .then(res => res.json())
      .then(cart => {
        // Clear all discount cookies & session/local storage
        document.cookie = 'discount_code=; path=/; max-age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT;';
        try {
          sessionStorage.removeItem('ib_cart_discount');
          sessionStorage.removeItem('discount_code');
          localStorage.removeItem('discount_code');
        } catch (e) {}

        if (this.discountHiddenInput) {
          this.discountHiddenInput.value = '';
        }

        this.showCouponMessage('Discount code removed.', 'info');
        this.renderCart(cart);
        this.syncHeaderCart(cart.item_count);
      })
      .catch(err => {
        console.error('Error removing coupon:', err);
        this.showCouponMessage('Could not remove coupon.', 'error');
        if (pill) pill.classList.remove('removing');
      });
    }

    sanitizePricingDisplays() {
      const subtotalPricing = document.getElementById('CartDrawerSubtotalPricing') || document.querySelector('.cart-drawer__subtotal-pricing');
      const checkoutPricing = document.getElementById('CartDrawerCheckoutPricing') || document.querySelector('.cart-drawer__checkout-pricing');
      
      if (!subtotalPricing) return;
      
      const subtotalOriginal = document.getElementById('CartDrawerSubtotalAmount');
      const checkoutOriginal = document.getElementById('CartDrawerCheckoutPrice');

      if (!this.hasShopifyDiscount) {
        if (subtotalOriginal && subtotalOriginal.style.textDecoration !== 'none') {
          subtotalOriginal.style.textDecoration = 'none';
        }
        const redundantNativeSpan = subtotalPricing.querySelector('.cart-drawer__discounted-total');
        if (redundantNativeSpan) redundantNativeSpan.remove();

        if (checkoutPricing) {
          if (checkoutOriginal && checkoutOriginal.style.textDecoration !== 'none') {
            checkoutOriginal.style.textDecoration = 'none';
          }
          const redundantCheckoutSpan = checkoutPricing.querySelector('.cart-drawer__checkout-discounted');
          if (redundantCheckoutSpan) redundantCheckoutSpan.remove();
        }
      }
    }

    showCouponMessage(text, type = 'info') {
      if (!this.couponMsg) return;
      this.couponMsg.textContent = text;
      this.couponMsg.className = `cart-drawer__coupon-msg ${type}`;
      setTimeout(() => {
        if (this.couponMsg && this.couponMsg.textContent === text) {
          this.couponMsg.textContent = '';
          this.couponMsg.className = 'cart-drawer__coupon-msg';
        }
      }, 4000);
    }

    renderCart(cart) {
      if (!this.drawer) return;
      if (!cart || cart.status || !Array.isArray(cart.items)) return;

      const itemCount = cart.item_count || 0;

      // Update count badge in drawer header
      if (this.countBadge) this.countBadge.textContent = itemCount;

      // Toggle Empty State vs Items List & Footer
      if (itemCount === 0) {
        if (this.emptyState) this.emptyState.classList.remove('hidden');
        if (this.itemsList) {
          this.itemsList.classList.add('hidden');
          this.itemsList.innerHTML = '';
        }
        if (this.footer) this.footer.classList.add('hidden');
        if (this.clearBtn) this.clearBtn.classList.add('hidden');
        this.syncHeaderCart(0);
        return;
      }

      if (this.emptyState) this.emptyState.classList.add('hidden');
      if (this.itemsList) this.itemsList.classList.remove('hidden');
      if (this.footer) this.footer.classList.remove('hidden');
      if (this.clearBtn) this.clearBtn.classList.remove('hidden');

      // 1. Identify Cart/Order-Level Discounts to avoid displaying them on line items
      const cartDiscountTitles = [];
      const normalizedCartDiscounts = [];

      // Auto-clean any lingering 'CLEAR' discount code if present in cart
      if (cart.discount_codes && cart.discount_codes.some(dc => dc.code && dc.code.toUpperCase() === 'CLEAR')) {
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ discount: '' })
        }).catch(() => {});
      }

      // Check cart_level_discount_applications (supports both /cart.js and /cart/change.js payloads)
      if (cart.cart_level_discount_applications && cart.cart_level_discount_applications.length > 0) {
        cart.cart_level_discount_applications.forEach(d => {
          const title = d.title || (d.discount_application && d.discount_application.title) || d.code || '';
          let amount = (typeof d.total_allocated_amount === 'number') 
            ? d.total_allocated_amount 
            : ((d.discount_application && typeof d.discount_application.total_allocated_amount === 'number')
              ? d.discount_application.total_allocated_amount
              : (typeof d.amount === 'number' ? d.amount : 0));
          if (amount === 0 && cart.original_total_price > cart.total_price) {
            amount = cart.original_total_price - cart.total_price;
          }
          if (title && title.toUpperCase() !== 'CLEAR') {
            const upper = title.toUpperCase();
            if (!cartDiscountTitles.map(t => t.toUpperCase()).includes(upper)) {
              cartDiscountTitles.push(title);
              normalizedCartDiscounts.push({ title, amount });
            }
          }
        });
      }

      // Also check cart.discount_codes (only applicable codes and not 'CLEAR')
      if (cart.discount_codes && cart.discount_codes.length > 0) {
        cart.discount_codes.forEach(dc => {
          if (dc.code && dc.applicable !== false && dc.code.toUpperCase() !== 'CLEAR') {
            const upper = dc.code.toUpperCase();
            if (!cartDiscountTitles.map(t => t.toUpperCase()).includes(upper)) {
              cartDiscountTitles.push(dc.code);
              const amount = dc.amount || (cart.original_total_price > cart.total_price ? (cart.original_total_price - cart.total_price) : 0);
              normalizedCartDiscounts.push({ title: dc.code, amount });
            }
          }
        });
      }

      // Clean up session if cart actually has no discounts
      if (normalizedCartDiscounts.length === 0) {
        sessionStorage.removeItem('ib_cart_discount');
        if (this.discountHiddenInput) {
          this.discountHiddenInput.value = '';
        }
      }

      // 2. Render Applied Discount Pills in Drawer Footer
      if (this.appliedDiscountsContainer) {
        if (normalizedCartDiscounts.length > 0) {
          this.appliedDiscountsContainer.innerHTML = normalizedCartDiscounts.map(d => `
            <div class="cart-drawer__applied-discount-pill" data-code="${d.title}">
              <span class="cart-drawer__applied-discount-icon">🏷️</span>
              <span class="cart-drawer__applied-discount-code">${d.title}</span>
              ${d.amount > 0 ? `<span class="cart-drawer__applied-discount-amount">(-${formatMoney(d.amount)})</span>` : ''}
              <button type="button" class="cart-drawer__applied-discount-remove" data-code="${d.title}" aria-label="Remove ${d.title}">
                <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" style="pointer-events: none;"><path d="M2 2l10 10M12 2L2 12"/></svg>
              </button>
            </div>
          `).join('');
        } else {
          this.appliedDiscountsContainer.innerHTML = '';
        }
      }

      // 3. Update Subtotal, Strikethrough Original Total & Savings
      const formattedTotal = formatMoney(cart.total_price);
      const hasShopifyDiscount = (cart.original_total_price > cart.total_price) || 
                                 (normalizedCartDiscounts.length > 0);
      this.hasShopifyDiscount = hasShopifyDiscount;

      const subtotalPricing = document.getElementById('CartDrawerSubtotalPricing') || (this.subtotalAmount ? this.subtotalAmount.parentElement : null);
      const checkoutPricing = document.getElementById('CartDrawerCheckoutPricing') || (this.checkoutPrice ? this.checkoutPrice.parentElement : null);

      if (this.subtotalAmount) {
        if (hasShopifyDiscount) {
          this.subtotalAmount.textContent = formatMoney(cart.original_total_price);
          this.subtotalAmount.style.textDecoration = 'line-through';
          let finalSpan = subtotalPricing ? subtotalPricing.querySelector('.cart-drawer__discounted-total') : null;
          if (!finalSpan && subtotalPricing) {
            finalSpan = document.createElement('span');
            finalSpan.className = 'cart-drawer__discounted-total';
            subtotalPricing.appendChild(finalSpan);
          }
          if (finalSpan) finalSpan.textContent = formattedTotal;
        } else {
          this.subtotalAmount.textContent = formattedTotal;
          this.subtotalAmount.style.textDecoration = 'none';
          if (subtotalPricing) {
            const finalSpan = subtotalPricing.querySelector('.cart-drawer__discounted-total');
            if (finalSpan) finalSpan.remove();
          }
        }
        this.subtotalAmount.classList.remove('price-updated');
        void this.subtotalAmount.offsetWidth;
        this.subtotalAmount.classList.add('price-updated');
      }

      if (this.checkoutPrice) {
        if (hasShopifyDiscount) {
          this.checkoutPrice.textContent = formatMoney(cart.original_total_price);
          this.checkoutPrice.style.textDecoration = 'line-through';
          let finalSpan = checkoutPricing ? checkoutPricing.querySelector('.cart-drawer__checkout-discounted') : null;
          if (!finalSpan && checkoutPricing) {
            finalSpan = document.createElement('span');
            finalSpan.className = 'cart-drawer__checkout-discounted';
            checkoutPricing.appendChild(finalSpan);
          }
          if (finalSpan) finalSpan.textContent = formattedTotal;
        } else {
          this.checkoutPrice.textContent = formattedTotal;
          this.checkoutPrice.style.textDecoration = 'none';
          if (checkoutPricing) {
            const finalSpan = checkoutPricing.querySelector('.cart-drawer__checkout-discounted');
            if (finalSpan) finalSpan.remove();
          }
        }
        this.checkoutPrice.classList.remove('price-updated');
        void this.checkoutPrice.offsetWidth;
        this.checkoutPrice.classList.add('price-updated');
      }

      const totalSaved = cart.original_total_price - cart.total_price;
      if (this.savingsBadge) {
        if (totalSaved > 0) {
          this.savingsBadge.innerHTML = `<span>🎉 You save <strong>${formatMoney(totalSaved)}</strong> on this order!</span>`;
          this.savingsBadge.classList.remove('hidden');
        } else {
          this.savingsBadge.classList.add('hidden');
        }
      }

      this.sanitizePricingDisplays();

      // 4. Re-render items (Filtering out repetitive cart-level discounts)
      let html = '';
      cart.items.forEach((item, index) => {
        // Only keep discounts that are specific to this product (NOT order/cart level discounts)
        const productSpecificDiscounts = (item.discounts || []).filter(d => {
          const dUpper = (d.title || '').toUpperCase();
          return !cartDiscountTitles.map(t => t.toUpperCase()).includes(dUpper);
        });
        const hasProductDiscount = productSpecificDiscounts.length > 0;
        const imgUrl = item.featured_image ? item.featured_image.url : (item.image || '');

        let discountsHtml = '';
        if (hasProductDiscount) {
          discountsHtml = '<div class="cart-drawer-item__discounts">' +
            productSpecificDiscounts.map(d => `<span class="cart-drawer-discount-tag">🏷️ ${d.title} (-${formatMoney(d.amount)})</span>`).join('') +
            '</div>';
        }

        let priceHtml = '';
        if (hasProductDiscount && item.original_line_price > item.final_line_price) {
          priceHtml = `
            <s class="cart-drawer-item__old-price">${formatMoney(item.original_line_price)}</s>
            <span class="cart-drawer-item__price has-discount">${formatMoney(item.final_line_price)}</span>
          `;
        } else {
          priceHtml = `<span class="cart-drawer-item__price">${formatMoney(item.original_line_price)}</span>`;
        }

        const variantHtml = item.variant_title && item.variant_title !== 'Default Title' 
          ? `<div class="cart-drawer-item__variant">${item.variant_title}</div>` 
          : '';

        html += `
          <div class="cart-drawer-item" id="CartDrawerItem-${item.key}" data-line-key="${item.key}" data-line-index="${index + 1}">
            <div class="cart-drawer-item__media">
              <a href="${item.url}">
                <img src="${imgUrl}" alt="${item.title}" width="80" height="80" loading="lazy" class="cart-drawer-item__image">
              </a>
            </div>
            <div class="cart-drawer-item__details">
              <div class="cart-drawer-item__top">
                <a href="${item.url}" class="cart-drawer-item__title">
                  ${item.product_title || item.title}
                </a>
                <button type="button" class="cart-drawer-item__remove-btn" data-key="${item.key}" aria-label="Remove item">
                  ${REMOVE_ICON_SVG}
                </button>
              </div>
              ${variantHtml}
              ${discountsHtml}
              <div class="cart-drawer-item__action-row">
                <div class="cart-drawer-item__stepper">
                  <button type="button" class="cart-drawer-item__step-btn step-minus" data-action="decrease" data-key="${item.key}" aria-label="Decrease quantity">
                    <span>-</span>
                  </button>
                  <input type="number" class="cart-drawer-item__qty-input" value="${item.quantity}" min="0" data-key="${item.key}" aria-label="Item quantity">
                  <button type="button" class="cart-drawer-item__step-btn step-plus" data-action="increase" data-key="${item.key}" aria-label="Increase quantity">
                    <span>+</span>
                  </button>
                </div>
                <div class="cart-drawer-item__price-box">
                  ${priceHtml}
                </div>
              </div>
            </div>
          </div>
        `;
      });

      if (this.itemsList) {
        this.itemsList.innerHTML = html;
      }

      this.sanitizePricingDisplays();
    }

    syncHeaderCart(count) {
      if (typeof window.syncCartHeaderBadge === 'function') {
        window.syncCartHeaderBadge(count);
      } else {
        const badges = document.querySelectorAll('#Cartcount, .cart-count, .cart-drawer__count-badge');
        badges.forEach(b => {
          b.textContent = count;
        });
      }
    }

    showToast(product) {
      if (!this.toast) return;

      clearTimeout(this.toastTimer);

      const title = product.title || product.product_title || 'Item';
      let img = product.image || (product.featured_image ? product.featured_image.url : '');

      if (this.toastProductName) {
        this.toastProductName.textContent = title;
      }

      if (this.toastImage) {
        if (img) {
          this.toastImage.src = img;
          this.toastImage.alt = title;
          this.toastImage.parentElement.style.display = 'block';
        } else {
          this.toastImage.parentElement.style.display = 'none';
        }
      }

      this.toast.classList.add('active');

      // Auto dismiss after 3.5 seconds
      this.toastTimer = setTimeout(() => {
        this.hideToast();
      }, 3500);
    }

    hideToast() {
      if (this.toast) {
        this.toast.classList.remove('active');
      }
      clearTimeout(this.toastTimer);
    }
  }

  // Global bulletproof Cart Header Badge & Count Synchronizer
  window.syncCartHeaderBadge = function(count) {
    const applyCount = (val) => {
      const numericCount = parseInt(val, 10) || 0;

      // Update all standard count badges
      const badges = document.querySelectorAll('#Cartcount, .cart-count, .cart-drawer__count-badge');
      badges.forEach(b => {
        b.textContent = numericCount;
      });

      // Update header wbhcart containers and ensure #Cartcount exists
      document.querySelectorAll('.wbhcart').forEach(container => {
        let cartCountEl = container.querySelector('#Cartcount');
        if (!cartCountEl) {
          cartCountEl = document.createElement('span');
          cartCountEl.id = 'Cartcount';
          cartCountEl.textContent = numericCount;
          const svgbg = container.querySelector('.svgbg');
          if (svgbg && svgbg.nextSibling) {
            container.insertBefore(cartCountEl, svgbg.nextSibling);
          } else {
            container.appendChild(cartCountEl);
          }
        } else {
          cartCountEl.textContent = numericCount;
        }

        // Pluralization for "item" / "items"
        const itemLabel = container.querySelector('span:not(#Cartcount):not(.svgbg)');
        if (itemLabel) {
          itemLabel.innerHTML = numericCount === 1 ? '&nbsp;item' : '&nbsp;items';
        }
      });

      // Update notification buttons if any
      document.querySelectorAll('#cart-notification-button').forEach(btn => {
        btn.textContent = `View Cart (${numericCount})`;
      });
    };

    if (typeof count === 'number' || (typeof count === 'string' && count !== '')) {
      applyCount(count);
    } else {
      fetch('/cart.js')
        .then(res => res.json())
        .then(cart => {
          applyCount(cart.item_count);
          if (window.cartDrawer && typeof window.cartDrawer.renderCart === 'function') {
            window.cartDrawer.renderCart(cart);
          }
        })
        .catch(() => {});
    }
  };

  // Initialize on DOM load
  document.addEventListener('DOMContentLoaded', () => {
    window.cartDrawer = new LuxuryCartDrawer();
  });

  // Re-bind on dynamic document updates
  window.addEventListener('load', () => {
    if (window.cartDrawer) window.cartDrawer.bindCartTriggers();
    if (typeof window.syncCartHeaderBadge === 'function') window.syncCartHeaderBadge();
  });

  // Re-sync when page is restored from bfcache
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      if (window.cartDrawer) window.cartDrawer.refresh();
      if (typeof window.syncCartHeaderBadge === 'function') window.syncCartHeaderBadge();
    }
  });

})();
